import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import pb from '../../../lib/pocketbase';

const Clients = () => {
    const [clients, setClients] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    // View / Edit / Create State
    const [viewingProfile, setViewingProfile] = useState(null);
    const [editingClient, setEditingClient] = useState(null);
    const [formData, setFormData] = useState({});

    // Fetch Data
    const fetchData = async () => {
        setLoading(true);
        try {
            const [usersRes, quotesRes, apptsRes] = await Promise.all([
                pb.collection('users').getFullList({ sort: '-created' }),
                pb.collection('quotes').getFullList({ sort: '-created' }),
                pb.collection('appointments').getFullList({ sort: '-created' })
            ]);

            setClients(usersRes);
            setQuotes(quotesRes);
            setAppointments(apptsRes);
        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Derived Data Calculation
    const getClientHistory = (userId, userEmail) => {
        const userQuotes = quotes.filter(q =>
            q.user === userId || (q.email && q.email.toLowerCase() === userEmail?.toLowerCase())
        );
        
        const userAppts = appointments.filter(a =>
            a.users === userId || (a.email && a.email.toLowerCase() === userEmail?.toLowerCase())
        );

        const vehicles = [
            ...userQuotes.map(q => `${q.vehicle_brand || ''} ${q.vehicle_model || ''} ${q.vehicle_plate ? `(${q.vehicle_plate})` : ''}`),
            ...userAppts.map(a => a.vehicle_details || '')
        ].filter(v => v && v.length > 2);
        
        const uniqueVehicles = [...new Set(vehicles)];

        return {
            quotes: userQuotes,
            appointments: userAppts,
            vehicles: uniqueVehicles
        };
    };

    // Handlers
    const handleCreate = () => {
        setEditingClient({ id: 'new' });
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address: ''
        });
    };

    const handleEdit = (client) => {
        setEditingClient(client);
        setFormData({
            firstName: client.firstName,
            lastName: client.lastName,
            email: client.email,
            phone: client.phone,
            address: client.address
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
        try {
            await pb.collection('users').delete(id);
            fetchData();
        } catch (err) {
            console.error(err);
            alert('Erreur lors de la suppression');
        }
    };

    const handleSave = async () => {
        try {
            const data = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                address: formData.address,
                // Default fields for user creation
                password: 'password123', // Placeholder, in real app should be handled better or sent invite
                passwordConfirm: 'password123',
                emailVisibility: true
            };

            if (editingClient.id === 'new') {
                await pb.collection('users').create(data);
            } else {
                // Remove password fields for update
                const { password, passwordConfirm, ...updateData } = data;
                await pb.collection('users').update(editingClient.id, updateData);
            }
            setEditingClient(null);
            fetchData();
        } catch (err) {
            console.error(err);
            alert('Erreur lors de la sauvegarde: ' + err.message);
        }
    };

    // Filtering
    const filteredClients = clients.filter(c =>
        (c.firstName + ' ' + c.lastName).toLowerCase().includes(search.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.phone || '').includes(search)
    );

    if (loading) return <div className="admin-content-wrapper">Chargement des clients...</div>;

    return (
        <div className="admin-content-wrapper">
            <section className="intro-card admin-intro">
                <div className="admin-header-content">
                    <h2>Clients & Véhicules</h2>
                    <p>Gérez votre base de données clients et leur parc automobile.</p>
                </div>
                <div className="admin-header-actions">
                    <button className="primary-button" onClick={handleCreate}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" y1="8" x2="19" y2="14"></line><line x1="22" y1="11" x2="16" y2="11"></line></svg>
                        Ajouter un Client
                    </button>
                    <button className="secondary-button" onClick={fetchData}>Actualiser</button>
                </div>
            </section>

            <section className="card full-width-card">
                <div className="filter-bar">
                    <div className="search-input-wrapper">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                        <input
                            type="text"
                            placeholder="Rechercher un client (nom, email, téléphone)..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="data-table admin-table">
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>Contact</th>
                                <th>Véhicules (Historique)</th>
                                <th>Demandes</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredClients.map((client) => {
                                const history = getClientHistory(client.id, client.email);
                                return (
                                    <tr key={client.id}>
                                        <td>
                                            <div className="user-cell">
                                                <div className="user-avatar-small">{(client.firstName?.[0] || '')}{(client.lastName?.[0] || '')}</div>
                                                <div className="user-name">{client.firstName} {client.lastName}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="user-info">
                                                <span className="user-sub" style={{ color: '#f1f5f9' }}>{client.phone || '-'}</span>
                                                <span className="user-sub">{client.email}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="vehicles-tags" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                {history.vehicles.length > 0 ? history.vehicles.slice(0, 3).map((v, i) => (
                                                    <span key={i} className="service-tag diag" style={{ fontSize: '0.7rem', width: 'fit-content' }}>{v}</span>
                                                )) : <span style={{ color: '#64748b', fontSize: '0.8rem' }}>-</span>}
                                            </div>
                                        </td>
                                        <td><strong>{history.quotes.length + history.appointments.length}</strong></td>
                                        <td className="text-right">
                                            <div className="action-buttons">
                                                <button
                                                    className="icon-btn"
                                                    title="Voir historique"
                                                    onClick={() => setViewingProfile({ ...client, history })}
                                                    style={{ color: '#facc15' }}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                                </button>
                                                <button
                                                    className="icon-btn"
                                                    title="Modifier"
                                                    onClick={() => handleEdit(client)}
                                                    style={{ color: '#3b82f6' }}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                                </button>
                                                <button
                                                    className="icon-btn"
                                                    title="Supprimer"
                                                    onClick={() => handleDelete(client.id)}
                                                    style={{ color: '#ef4444' }}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* VIEW PROFILE MODAL */}
            {viewingProfile && createPortal(
                <div className="modal-backdrop" onClick={() => setViewingProfile(null)} style={{ zIndex: 99999 }}>
                    <div className="modal-content profile-modal-neo" onClick={e => e.stopPropagation()}>
                        <div className="profile-hero-section">
                            <button className="close-profile-btn" onClick={() => setViewingProfile(null)}>×</button>
                            <div className="profile-hero-content">
                                <div className="profile-avatar-large">
                                    {(viewingProfile.firstName?.[0] || '')}{(viewingProfile.lastName?.[0] || '')}
                                </div>
                                <div className="profile-main-meta">
                                    <h1>{viewingProfile.firstName} {viewingProfile.lastName}</h1>
                                    <div className="profile-badges">
                                        <span className="id-tag">Client ID: {viewingProfile.id}</span>
                                        <span className="loyalty-tag">✨ Client Fidèle</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="profile-grid-layout">
                            {/* Sidebar Info */}
                            <div className="profile-aside">
                                <div className="info-group-card">
                                    <h4><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg> Coordonnées</h4>
                                    <div className="contact-links">
                                        <a href={`mailto:${viewingProfile.email}`} className="contact-link-item">
                                            <span className="icon">📧</span>
                                            <div className="text">
                                                <span className="label">E-mail</span>
                                                <span className="value">{viewingProfile.email}</span>
                                            </div>
                                        </a>
                                        <a href={`tel:${viewingProfile.phone}`} className="contact-link-item">
                                            <span className="icon">📞</span>
                                            <div className="text">
                                                <span className="label">Téléphone</span>
                                                <span className="value">{viewingProfile.phone || 'Non renseigné'}</span>
                                            </div>
                                        </a>
                                        <div className="contact-link-item">
                                            <span className="icon">📍</span>
                                            <div className="text">
                                                <span className="label">Adresse</span>
                                                <span className="value">{viewingProfile.address || 'Aucune adresse renseignée'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="info-group-card">
                                    <h4><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L19 10V4c0-.6-.4-1-1-1H6c-.6 0-1 .4-1 1v6l-2.5 1.1c-.8.2-1.5 1-1.5 1.9v3c0 .6.4 1 1 1h2"></path><rect x="7" y="17" width="10" height="4" rx="1"></rect><path d="M7 12h10"></path><path d="M7 8h10"></path></svg> Parc Automobile</h4>
                                    <div className="vehicle-fleet">
                                        {viewingProfile.history.vehicles.map((v, i) => (
                                            <div key={i} className="fleet-card">
                                                <span className="car-icon">🚗</span>
                                                <span className="car-desc">{v}</span>
                                            </div>
                                        ))}
                                        {viewingProfile.history.vehicles.length === 0 && <p className="empty-state-sm">Aucun véhicule enregistré</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Main Content History */}
                            <div className="profile-main-content">
                                <div className="history-tab-section">
                                    <div className="section-title-row">
                                        <h3>Historique & Agenda</h3>
                                        <span className="count-badge">{viewingProfile.history.appointments.length + viewingProfile.history.quotes.length} Événements</span>
                                    </div>

                                    <div className="timeline-container">
                                        {/* Prochains RDV */}
                                        <div className="timeline-group">
                                            <h5>PROCHAINS RENDEZ-VOUS</h5>
                                            <div className="timeline-items">
                                                {viewingProfile.history.appointments.map(a => (
                                                    <div key={a.id} className={`timeline-item-card ${a.status}`}>
                                                        <div className="tm-date">
                                                            <span className="d">{a.date.split('-')[2]}</span>
                                                            <span className="m">{new Date(a.date).toLocaleString('fr', { month: 'short' })}</span>
                                                        </div>
                                                        <div className="tm-content">
                                                            <div className="tm-header">
                                                                <strong>{a.service}</strong>
                                                                <span className={`status-tag ${a.status}`}>
                                                                    {a.status === 'pending' ? 'Attente' : a.status}
                                                                </span>
                                                            </div>
                                                            <p className="tm-sub">{a.time} • {a.vehicle_details}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                                {viewingProfile.history.appointments.length === 0 && <p className="empty-state-sm">Aucun rendez-vous prévu</p>}
                                            </div>
                                        </div>

                                        {/* Devis & Demandes */}
                                        <div className="timeline-group" style={{ marginTop: '30px' }}>
                                            <h5>DEVIS & DEMANDES</h5>
                                            <div className="timeline-items">
                                                {viewingProfile.history.quotes.map(q => (
                                                    <div key={q.id} className="timeline-item-card quote">
                                                        <div className="tm-date quote-tm">
                                                            <span className="d">{new Date(q.created).getDate()}</span>
                                                            <span className="m">{new Date(q.created).toLocaleString('fr', { month: 'short' })}</span>
                                                        </div>
                                                        <div className="tm-content">
                                                            <div className="tm-header">
                                                                <strong>Devis #{q.id.slice(-4)}</strong>
                                                                <span className="quote-price-tag">{q.total_price}€</span>
                                                            </div>
                                                            <p className="tm-sub">{q.vehicle_brand} {q.vehicle_model}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                                {viewingProfile.history.quotes.length === 0 && <p className="empty-state-sm">Aucun devis historique</p>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* EDIT/CREATE CLIENT MODAL */}
            {editingClient && createPortal(
                <div className="modal-backdrop" onClick={() => setEditingClient(null)} style={{ zIndex: 99999 }}>
                    <div className="modal-content admin-modal-large" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="admin-modal-header">
                            <h2>{editingClient.id === 'new' ? 'Nouveau Client' : `Éditer ${formData.firstName} ${formData.lastName}`}</h2>
                            <button className="modal-close" onClick={() => setEditingClient(null)}>×</button>
                        </div>
                        <div className="admin-modal-body" style={{ padding: '2rem' }}>
                            <div className="pricing-input-grid">
                                <div className="form-group">
                                    <label>Prénom</label>
                                    <input className="admin-input" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Nom</label>
                                    <input className="admin-input" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Email</label>
                                    <input className="admin-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Téléphone</label>
                                    <input className="admin-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                                </div>
                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                    <label>Adresse</label>
                                    <textarea className="admin-input" rows="2" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="secondary-button" onClick={() => setEditingClient(null)}>Annuler</button>
                            <button className="primary-button" onClick={handleSave}>Enregistrer</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Clients;
